import { Alert, Box, Heading, Skeleton, TextInput } from '@razorpay/blade/components';
import xGrey from 'assets/mss/x-grey.svg';
import React, { useState } from 'react';
import { useSalaryStructure } from '../hooks/useSalaryStructure';
import PreviewCtcTable from './PreviewCtcTable';
import { useQuery } from 'react-query';
import { salaryStructureQueries } from 'components/SalaryStructures/constants';
import api from 'api';
import { format } from 'date-fns';
import { useDebouncedValue } from 'hooks/useDebouncedValue';
import { SalaryStructureComponent } from 'components/SalaryStructure/types';
import { AppError } from 'utils/AppError';
import { getPreviewCTCData } from '../utils';

export const PreviewCtc = ({ isPreviewHidden = false }: { isPreviewHidden?: boolean }) => {
  const { salaryStructureComponents, formData } = useSalaryStructure();

  const { debouncedValue: debouncedComponents } = useDebouncedValue(
    JSON.stringify(salaryStructureComponents),
    500,
  );
  const [compensation, setCompensation] = useState('1200000');

  const { debouncedValue: debouncedCompensation } = useDebouncedValue(compensation, 500);

  const { data, isFetching, error } = useQuery({
    queryKey: [
      salaryStructureQueries.getSalaryComponentSDIs,
      formData?.templateId?.[0],
      debouncedCompensation,
      debouncedComponents,
    ],
    queryFn: async () => {
      const parsedSalaryComponents: SalaryStructureComponent[] = JSON.parse(debouncedComponents);

      const inputs = parsedSalaryComponents?.map(({ salaryComponentId, formula, valueType }) => {
        // For FIXED type: user typed values are in rupees, but x-salary-structure treats FIXED formulas as paise.
        // For CTC/BASIC/SA types: formula is a percentage or residual — no unit conversion needed.
        const isFixed = valueType === 'SALARY_STRUCTURE_COMPONENT_VALUE_TYPE_FIXED';
        const effectiveFormula =
          valueType === 'SALARY_STRUCTURE_COMPONENT_VALUE_TYPE_SA'
            ? formula
            : !!formula
            ? isFixed
              ? (parseInt(formula, 10) * 100).toString()
              : formula
            : '0';
        return {
          salaryComponentId,
          formula: effectiveFormula,
          valueType,
        };
      });

      const result = await api.salaryStructures.queries.getSalaryComponentSDIs({
        input: {
          requests: inputs,
          compensation: String(Number(debouncedCompensation || '0') * 100),
          payrollMonth: format(Date.now(), 'yyyy-MM'),
          complianceSettings: {
            pfApplicable: false,
            esiApplicable: false,
            lwfApplicable: false,
            ptApplicable: false,
          },
        },
      });

      const payrollStagingData = result?.payrollGetSalaryComponentSDIs?.data?.payrolls?.[0]?.data;
      return getPreviewCTCData(payrollStagingData);
    },

    enabled: !isPreviewHidden && salaryStructureComponents?.length > 0,
  });

  const fixedEarnings = data?.fixed;

  const variableEarning = data?.variable;

  const nonPayableBenefitsEarning = data?.otherCtc;

  return (
    <Box
      borderRadius={isPreviewHidden ? 'none' : 'medium'}
      borderWidth={isPreviewHidden ? 'none' : 'thin'}
      borderColor="surface.border.gray.muted"
      backgroundImage={`url("${xGrey}")`}
      backgroundRepeat="no-repeat"
      backgroundPosition="right bottom"
      height="100%"
      overflow="auto">
      <Box
        padding="spacing.6"
        gridTemplateRows="auto auto 1fr"
        display={isPreviewHidden ? 'none' : 'grid'}>
        <Heading weight="medium">Preview with sample CTC 🔥</Heading>

        <Box position="sticky" top="spacing.0" marginY="spacing.6">
          <TextInput
            prefix="₹"
            type="number"
            value={compensation}
            placeholder="1200000"
            size="large"
            suffix="Per year"
            onChange={({ value }) => setCompensation(value as string)}
            label=""
          />
        </Box>

        {!!error ? (
          <Alert description={(error as AppError).message} color="negative" isDismissible={false} />
        ) : (
          <Box
            overflow="auto"
            borderRadius="medium"
            borderWidth="thin"
            borderColor="surface.border.gray.muted">
            {isFetching ? (
              <Skeleton height={'100%'} width={'100%'} />
            ) : (
              <>
                <PreviewCtcTable
                  earning={fixedEarnings}
                  columnTitles={['Fixed', 'Yearly Amount']}
                />
                <PreviewCtcTable earning={variableEarning} columnTitles={['Variable', '']} />
                <PreviewCtcTable earning={nonPayableBenefitsEarning} columnTitles={['Other', '']} />
              </>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};
